// Instagram-Säulen (Signal-Room phase 1, PLAN #12) — read-mostly catalog of the imported
// Evidenz-Säulen: rank/claim/status/facts/coverage plus the hide-toggle (#9). Deliberately a
// SEPARATE page from MarketingStrategy.tsx — the LinkedIn/strategy pillar system stays untouched.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Instagram, ChevronDown, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

type PillarFact = { text: string; source?: string | null };
type SrPillarRow = {
  id: string;
  rank: number;
  name: string;
  normalizedName: string;
  claim: string | null;
  status: string | null;
  factsJson: PillarFact[];
  series: string | null;
  cadence: string | null;
  seedIdeasJson: string[];
  keywordsJson: string[];
  coverageJson: Record<string, unknown>;
  importedAt: string;
  hidden: boolean;
};

const QUERY_KEY = ["/api/signal-room/instagram-pillars"];

const statusConfig: Record<string, { label: string; className: string }> = {
  "never-posted": { label: "Nie bespielt", className: "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300" },
  dormant: { label: "Eingeschlafen", className: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300" },
  underused: { label: "Untergenutzt", className: "bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300" },
  workhorse: { label: "Zugpferd", className: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300" },
};

function coverageCount(coverage: Record<string, unknown>): number | null {
  const v = (coverage as any)?.count ?? (coverage as any)?.posts;
  return typeof v === "number" ? v : null;
}

// Imported jsonb is exporter-shaped; render list entries defensively so an object entry
// (e.g. a {title, angle} seed idea) never surfaces as "[object Object]".
function asText(entry: unknown): string {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") {
    const o = entry as Record<string, unknown>;
    const parts = [o.title, o.angle, o.text].filter((x) => typeof x === "string" && x);
    if (parts.length) return parts.join(" · ");
  }
  return JSON.stringify(entry);
}

export default function InstagramSaeulen() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const { data, isLoading } = useQuery<{ mapped: boolean; pillars: SrPillarRow[] }>({
    queryKey: QUERY_KEY,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const toggleHidden = useMutation({
    mutationFn: ({ normalizedName, hidden }: { normalizedName: string; hidden: boolean }) =>
      apiRequest("POST", "/api/signal-room/instagram-pillars/prefs", { normalizedName, hidden }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    onError: (err: any) => {
      toast({
        title: "Änderung fehlgeschlagen",
        description: err?.data?.error || err?.message || "Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    },
  });

  const pillars = data?.pillars ?? [];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Instagram className="h-8 w-8 text-pink-600" />
        <div>
          <h1 className="text-2xl font-bold">Instagram-Säulen</h1>
          <p className="text-sm text-muted-foreground">
            Ihre Evidenz-Säulen aus der Markt- und Archiv-Analyse: die Themenbasis für Ihre Instagram-Beiträge.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      ) : !data?.mapped || pillars.length === 0 ? (
        <Card data-testid="card-sr-pillars-empty">
          <CardContent className="p-8 text-center text-muted-foreground">
            Noch keine Instagram-Säulen vorhanden. Sie erscheinen hier, sobald die erste Analyse
            für Ihr Hotel importiert wurde.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {pillars.map((pillar) => {
            const status = pillar.status ? statusConfig[pillar.status] : null;
            const isOpen = !!expanded[pillar.id];
            const posts = coverageCount(pillar.coverageJson);
            return (
              <Card key={pillar.id} className={pillar.hidden ? "opacity-50" : ""} data-testid={`card-sr-pillar-${pillar.rank}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      className="mt-1 text-muted-foreground"
                      onClick={() => setExpanded((e) => ({ ...e, [pillar.id]: !isOpen }))}
                      data-testid={`button-expand-${pillar.rank}`}
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="text-xs">#{pillar.rank}</Badge>
                        <span className="font-semibold">{pillar.name}</span>
                        {status && <Badge className={`text-xs border w-fit ${status.className}`}>{status.label}</Badge>}
                        {posts !== null && (
                          <span className="text-xs text-muted-foreground">{posts} {posts === 1 ? "Beitrag" : "Beiträge"} bisher</span>
                        )}
                      </div>
                      {pillar.claim && <p className="text-sm text-muted-foreground mt-1">{pillar.claim}</p>}
                      {isOpen && (
                        <div className="mt-3 space-y-3 text-sm">
                          {pillar.factsJson.length > 0 && (
                            <div>
                              <div className="font-medium mb-1">Fakten</div>
                              <ul className="list-disc pl-5 space-y-1">
                                {pillar.factsJson.map((f, i) => (
                                  <li key={i}>
                                    {typeof f?.text === "string" ? f.text : asText(f)}
                                    {f?.source && <span className="text-muted-foreground"> ({f.source})</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {(pillar.series || pillar.cadence) && (
                            <div className="text-muted-foreground">
                              {pillar.series && <span>Serie: {pillar.series}</span>}
                              {pillar.series && pillar.cadence && <span> · </span>}
                              {pillar.cadence && <span>Takt: {pillar.cadence}</span>}
                            </div>
                          )}
                          {pillar.seedIdeasJson.length > 0 && (
                            <div>
                              <div className="font-medium mb-1">Ideen</div>
                              <ul className="list-disc pl-5 space-y-1">
                                {pillar.seedIdeasJson.map((idea, i) => <li key={i}>{asText(idea)}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground">Sichtbar</span>
                      <Switch
                        checked={!pillar.hidden}
                        onCheckedChange={(visible) =>
                          toggleHidden.mutate({ normalizedName: pillar.normalizedName, hidden: !visible })
                        }
                        data-testid={`switch-pillar-visible-${pillar.rank}`}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
